import type { ReactElement } from 'react';
import styled, { css } from 'styled-components';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import { createReadableClass } from '@elfsight/widget-utils';
import { MAX_VISIBLE_BULLETS_COUNT, MIN_BULLETS_COUNT } from '../constants';
import { calculateBulletsCount } from './calculateBulletsCount';
import { calculateBulletTranslate } from './calculateBulletTranslate';

export interface BulletsControlProps {
  currentItemIndex: number;
  items: ReactElement[];
  loop: boolean;
  itemsPerSlide: number;
  slideBy: 'item' | 'slide';
  position: 'inside' | 'outside';
  color: string;
  bulletSize?: number;
  bulletGap?: number;
  backgroundColor?: string;
  backgroundPadding?: number;
  backgroundCornerRadius?: number;
  forceStaticBigBullet?: boolean;
  insideGap?: number;
  rtl?: boolean;
  onSlideTo: (itemIndex: number, speed?: number) => void;
}

export function BulletsControl({
  currentItemIndex: derivedCurrentItemIndex,
  loop,
  items,
  itemsPerSlide,
  slideBy,
  position,
  color,
  backgroundColor,
  bulletSize = 6,
  bulletGap = 4,
  insideGap = 8,
  forceStaticBigBullet = false,
  backgroundPadding = 4,
  backgroundCornerRadius = 30,
  rtl,
  onSlideTo
}: BulletsControlProps) {
  const isSlideMode = slideBy === 'slide';
  const slideStep = isSlideMode ? itemsPerSlide : 1;
  const currentItemIndex = Math.ceil(derivedCurrentItemIndex / slideStep);
  const bulletsCount = calculateBulletsCount({
    slideStep,
    loop,
    itemsLength: items.length,
    pageSize: itemsPerSlide
  });

  const finalBackgroundPadding = backgroundColor ? backgroundPadding : 0;
  const sizeDotWithGap = bulletSize + bulletGap;

  const translate = calculateBulletTranslate(
    currentItemIndex,
    bulletsCount,
    sizeDotWithGap
  );

  const handleClick = (index: number) => {
    onSlideTo(index * slideStep);
  };

  const calculatedPaginationWidth = getPaginationWidth(
    bulletSize,
    bulletsCount,
    bulletGap,
    finalBackgroundPadding
  );
  const maxPaginationWidth = getPaginationWidth(
    bulletSize,
    MAX_VISIBLE_BULLETS_COUNT,
    bulletGap,
    finalBackgroundPadding
  );

  if (bulletsCount < MIN_BULLETS_COUNT - 1) {
    return null;
  }

  return (
    <BulletsControlContainer
      style={{
        width: Math.min(calculatedPaginationWidth, maxPaginationWidth),
        position: position === 'inside' ? 'absolute' : undefined,
        bottom: position === 'inside' ? insideGap : undefined,
        transform: rtl ? 'scaleX(-1)' : ''
      }}
      _backgroundColor={backgroundColor}
      _backgroundPadding={backgroundPadding}
      _backgroundCornerRadius={backgroundCornerRadius}
    >
      <BulletsControlInner
        style={{
          transform: `translate(${translate}px, 0px)`
        }}
        _gap={bulletGap}
      >
        {Array.from({ length: bulletsCount }).map((_, index) => {
          const distance = Math.abs(index - currentItemIndex);

          return (
            <BulletsControlItem
              key={index * slideStep}
              _size={bulletSize}
              _active={index === currentItemIndex}
              _color={color}
              _distance={forceStaticBigBullet ? 0 : distance}
              _bulletGap={bulletGap}
              onClick={() => handleClick(index)}
            />
          );
        })}
      </BulletsControlInner>
    </BulletsControlContainer>
  );
}

const getScale = (distance: number) => {
  // Учитываем расстояние от 0 до 3
  const clamped = Math.min(Math.abs(distance), 3);
  if (clamped === 0) return 1;
  if (clamped === 1) return 0.75;
  if (clamped === 2) return 0.5;
  return 0.25;
};

function getPaginationWidth(
  dotSize: number,
  bulletsCount: number,
  dotGap: number,
  containerPadding: number
) {
  const dotSizeWitGap = dotSize + dotGap;

  return dotSizeWitGap * bulletsCount - dotGap + containerPadding * 2;
}

const BulletsControlContainer = styled.div.attrs({
  className: createReadableClass({
    component: 'bullets-control',
    element: 'container'
  })
})<{
  _backgroundColor: string | undefined;
  _backgroundPadding: number;
  _backgroundCornerRadius: number;
}>`
  display: flex;
  z-index: 1;
  overflow: hidden;

  ${({ _backgroundColor, _backgroundCornerRadius, _backgroundPadding }) =>
    _backgroundColor &&
    css`
      background-color: ${_backgroundColor};
      border-radius: ${_backgroundCornerRadius}px;
      padding: ${_backgroundPadding}px;
    `};
`;

const BulletsControlInner = styled.div.attrs({
  className: createReadableClass({
    component: 'bullets-control',
    element: 'inner'
  })
})<{
  _gap?: number;
}>`
  display: inline-flex;
  transition-duration: 300ms;
  align-items: flex-start;
  gap: ${({ _gap }) => _gap}px;
`;

const BulletsControlItem = styled.div.attrs<{
  _size: number;
  _active: boolean;
  _color: string;
  _distance: number;
  _bulletGap: number;
}>(({ _active }) => ({
  className: createReadableClass({
    component: 'bullets-control',
    element: 'item',
    index: _active && 'active'
  })
}))`
  width: ${({ _size }) => _size}px;
  min-width: ${({ _size }) => _size}px;
  height: ${({ _size }) => _size}px;
  background: ${({ _active, _color }) =>
    _active ? _color : getColorWithAlpha(_color, 0.5)};
  border-radius: 50%;
  transition: transform 0.3s ease;
  transform: ${({ _distance }) => `scale(${getScale(_distance)})`};

  &:hover {
    background: ${({ _color }) => _color};
  }

  &:after {
    content: '';
    z-index: 1;
    cursor: pointer;
    position: absolute;
    inset: ${({ _bulletGap }) => -_bulletGap / 2}px;
  }
`;
